/**
 * Entities Index - Betrieb Müller
 *
 * Centralized access to all Digital Twin entities.
 */

import type { MockEntity } from '@hofsync/design-system';

// Import all entity collections
import { ALL_FIELDS, getFieldById, getFieldsNeedingSoilSample, FIELD_GROSSES_FELD, FIELD_HINTERFELD, FIELD_MOORWIESE, FIELD_WALDACKER } from './fields';
import {
  ALL_ANIMALS, getAnimalById, getAnimalGroupById, getTotalGV, calculateGV,
  ANIMAL_BELLA, ANIMAL_ROSIE, ANIMAL_BRUNO,
  ANIMAL_PIG_101, ANIMAL_PIG_102, ANIMAL_SAU_200,
  ANIMALS_MASTSCHWEINE, ANIMALS_MILCHKUEHE,
} from './animals';
import { ALL_EQUIPMENT, getEquipmentById, getEquipmentWithExpiringInspection, EQUIPMENT_GUELLEFASS, EQUIPMENT_TRAKTOR, EQUIPMENT_FELDSPRITZE, EQUIPMENT_ANHAENGER } from './equipment';
import {
  ALL_PERSONS, getPersonById, getPersonsWithExpiringCerts, getPersonsNeedingBriefing,
  PERSON_KLAUS, PERSON_THOMAS, PERSON_KRUEGER, PERSON_SCHULZE, PERSON_BRANDT, PERSON_FISCHER, PERSON_BERGMANN,
  // deprecated re-exports
  ALL_EMPLOYEES, EMPLOYEE_KLAUS, EMPLOYEE_THOMAS, getEmployeeById, getEmployeesWithExpiringCerts, getEmployeesNeedingBriefing,
} from './employees';
import {
  ALL_ORGANIZATIONS, getOrganizationById,
  ORG_RAIFFEISEN, ORG_BAYWA, ORG_FRUTANIA, ORG_GETREIDEHANDEL_MUELLER, ORG_GUT_SONNENBERG, ORG_ROSENBAUM,
} from './organizations';
import { ALL_ENTITY_EVENTS, getEntityEventsById } from './entity-events';

/**
 * Get all entities across all types
 */
export function getAllEntities(): MockEntity[] {
  return [...ALL_FIELDS, ...ALL_ANIMALS, ...ALL_EQUIPMENT, ...ALL_PERSONS, ...ALL_ORGANIZATIONS];
}

/**
 * Get entity by ID (searches all entity types)
 */
export function getEntityById(id: string): MockEntity | undefined {
  return (
    getFieldById(id) ||
    getAnimalById(id) ||
    getEquipmentById(id) ||
    getPersonById(id) ||
    getOrganizationById(id)
  );
}

/**
 * Get entities by type
 */
export function getEntitiesByType(type: MockEntity['type']): MockEntity[] {
  switch (type) {
    case 'field':
      return ALL_FIELDS;
    case 'animal':
      return ALL_ANIMALS;
    case 'equipment':
      return ALL_EQUIPMENT;
    case 'person':
      return ALL_PERSONS;
    case 'organization':
      return ALL_ORGANIZATIONS;
    default:
      return [];
  }
}

/**
 * Get entities with warning status
 */
export function getEntitiesWithWarnings(): MockEntity[] {
  return getAllEntities().filter((e) => e.status === 'warning');
}

// Re-export everything for convenience
export {
  // Fields
  ALL_FIELDS,
  getFieldById,
  getFieldsNeedingSoilSample,
  FIELD_GROSSES_FELD,
  FIELD_HINTERFELD,
  FIELD_MOORWIESE,
  FIELD_WALDACKER,
  // Animals
  ALL_ANIMALS,
  getAnimalById,
  getTotalGV,
  calculateGV,
  ANIMAL_BELLA,
  ANIMAL_ROSIE,
  ANIMAL_BRUNO,
  ANIMAL_PIG_101,
  ANIMAL_PIG_102,
  ANIMAL_SAU_200,
  // Deprecated animal aliases
  getAnimalGroupById,
  ANIMALS_MASTSCHWEINE,
  ANIMALS_MILCHKUEHE,
  // Equipment
  ALL_EQUIPMENT,
  getEquipmentById,
  getEquipmentWithExpiringInspection,
  EQUIPMENT_GUELLEFASS,
  EQUIPMENT_TRAKTOR,
  EQUIPMENT_FELDSPRITZE,
  EQUIPMENT_ANHAENGER,
  // Persons
  ALL_PERSONS,
  getPersonById,
  getPersonsWithExpiringCerts,
  getPersonsNeedingBriefing,
  PERSON_KLAUS,
  PERSON_THOMAS,
  PERSON_KRUEGER,
  PERSON_SCHULZE,
  PERSON_BRANDT,
  PERSON_FISCHER,
  PERSON_BERGMANN,
  // Organizations
  ALL_ORGANIZATIONS,
  getOrganizationById,
  ORG_RAIFFEISEN,
  ORG_BAYWA,
  ORG_FRUTANIA,
  ORG_GETREIDEHANDEL_MUELLER,
  ORG_GUT_SONNENBERG,
  ORG_ROSENBAUM,
  // Deprecated aliases
  ALL_EMPLOYEES,
  EMPLOYEE_KLAUS,
  EMPLOYEE_THOMAS,
  getEmployeeById,
  getEmployeesWithExpiringCerts,
  getEmployeesNeedingBriefing,
  // Entity events
  ALL_ENTITY_EVENTS,
  getEntityEventsById,
};
